import React from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { TenantProductName } from '@recurrency/core-api-schema/dist/common/enums';
import { Empty } from 'antd';
import { theme } from 'theme';

import { Card, CardHeader, CardSection } from 'components/Card';
import { Typography } from 'components/Typography';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { showAsyncModal } from 'utils/asyncModal';
import { formatPercent } from 'utils/formatting';
import { shouldShowProduct } from 'utils/roleAndTenant';

import { CardLoader } from '../shared/CardLoader';
import { ExploreButton } from '../shared/ExploreButton';
import { inventoryBriefCategories, InventoryBriefCategoryConfig } from './briefCategories';
import { InventoryBriefCategoryModal } from './InventoryBriefCategoryModal';

export function InventoryBriefSection() {
  const { activeTenant } = useGlobalApp();
  const isFreeReporting = !shouldShowProduct(activeTenant, TenantProductName.EnterpriseBasic);
  const {
    data: briefData,
    error: briefError,
    isLoading: briefIsLoading,
  } = useCoreApi(schemas.inventory.getInventoryBrief, {});

  const openCategoryModal = (category: InventoryBriefCategoryConfig) => {
    showAsyncModal(InventoryBriefCategoryModal, { category });
  };

  return (
    <>
      <Typography type="h3" style={{ fontWeight: 600, marginBottom: 12 }}>
        Inventory Brief
      </Typography>
      <div
        className={css`
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 18px;
        `}
      >
        {inventoryBriefCategories.map((category) => {
          const rows = briefData ? category.getPanelRows(briefData, !isFreeReporting) : [];
          return (
            <Card key={category.key}>
              <CardHeader
                title={category.title}
                description={category.description}
                useTooltip
                className={
                  !isFreeReporting
                    ? css`
                        cursor: pointer;
                      `
                    : ''
                }
                onClick={isFreeReporting ? undefined : () => openCategoryModal(category)}
                headerActions={isFreeReporting ? undefined : <ExploreButton />}
              />
              {briefError ? (
                <CardSection>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Failed to load section. Please try again later."
                  />
                </CardSection>
              ) : briefIsLoading ? (
                <CardSection>
                  <CardLoader height={200} />
                </CardSection>
              ) : rows.length === 0 ? (
                <CardSection>
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing to report" />
                </CardSection>
              ) : (
                rows.map((row) => (
                  <CardSection
                    key={row.id}
                    className={css`
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      gap: 8px;
                    `}
                  >
                    <div
                      className={css`
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                      `}
                    >
                      {row.name}
                    </div>
                    <div
                      className={css`
                        flex-shrink: 0;
                        display: flex;
                        align-items: center;
                        gap: 4px;
                      `}
                    >
                      {row.change}
                      {row.changePercent !== undefined && (
                        <span
                          className={css`
                            color: ${theme.colors.neutral[400]};
                          `}
                        >
                          ({formatPercent(row.changePercent, 0)})
                        </span>
                      )}
                    </div>
                  </CardSection>
                ))
              )}
            </Card>
          );
        })}
      </div>
    </>
  );
}
